import type { ObjectColor } from '@ofmos/shared/object-color';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { type DrawingName, isDrawingName, MonoSymbol } from '../ObjectGlyph/MonoSymbol';
import { type GlyphName, isGlyphName, ObjectGlyph } from '../ObjectGlyph/ObjectGlyph';

/**
 * THE COLOURED TILE IN FRONT OF AN OBJECT'S NAME (`ui-foundation.md` E9).
 *
 * iOS and macOS System Settings put a small rounded square with a white glyph in front
 * of every row, and that is what makes a list of twenty settings scannable: the eye
 * finds a colour before it reads a word, so the colour is a HANDLE for coming back to a
 * row, not a status and not decoration. Our sidebar has fourteen object rows with the
 * same problem.
 *
 * THE TILE IS THE KIT'S SERVICE ICON (2026-09-08, after the design session found it in
 * the macOS 27 kit as "Forms/Rows - Leading/Sidebar Icon/Service Icon" on the layer style
 * "Forms/Rows - Leading/Icon Background"): a vertical gradient one kit step lighter at
 * the top than at the bottom, and the symbol on it. The stops are the `-top` tokens
 * beside the twelve tile colours in `tokens.css`, the interpolation is sRGB like the kit
 * and the document, and the corner radius is Apple's 10/57 of the edge on
 * `--radius-tile-sm` and `--radius-tile-md`. A tile is not allowed a raw size, radius or
 * colour (DESIGN.md), so all of them are read by token name.
 *
 * WHAT IT DRAWS: one of the thirty-one SYMBOLS (`ObjectGlyph`, symbol set v2 - the
 * fifteen object symbols Arif approved one per object, and the sixteen function-row
 * symbols) or, for a row that has none, a hairline `Icon`. A symbol fills the tile - the
 * artwork is drawn to the grid at 70 to 80 percent, as Apple's is - and the hairline icon
 * keeps the 14px and 16px `--tile-glyph-*` sizes it always had.
 *
 * WHOSE COLOUR: the operator's, then the symbol's, then graphite. An object colour
 * chosen on the settings page paints the tile in that system colour (E9, unchanged).
 * With no colour chosen, a symbol brings ITS OWN pair - the one Arif decided for it
 * (the Health heart's red for the pulse, the OnlyFans blue for the creator, Podcasts'
 * purple for the deal, Keynote's blue under the trophy, Files' blue for deliveries,
 * Siri's white for the AI helmet, System Gray for the person; the other eight are system
 * colours), measured on Apple's artwork and held as `--color-tile-<symbol>` tokens. A
 * hairline icon with no colour gets the neutral graphite tile, so every row has one.
 *
 * FOUR STYLES, AND THE PERSON PICKS ONE (2026-09-09, the sidebar-symbols package).
 * Apple's "Icon & widget style" - Default, Dark, Clear, Tinted - applies to every app
 * icon and settings tile on a Mac and an iPhone, and this tile is one of those. The
 * component does not know which is on: it hands the stylesheet a colour PAIR through
 * `--tile-color` / `--tile-color-top` and the four looks live in `index.css`, keyed on
 * the `data-symbol-style` attribute `app/session.ts` resolves onto `<html>`. That split
 * is the point - a look changes the ground, the symbol's colour and its effect stack at
 * once, and none of that is a property of one call site.
 *
 * THE GLYPH IS WHITE IN BOTH THEMES under the Default style, which is what Apple does, so
 * it reads `--color-tile-glyph` and NOT `--color-ink-inverted`: the inverted ink FLIPS with the
 * theme and would put a near-black symbol on a bright tile in the dark (caught by the
 * token contrast test, not by looking). The symbol's body stops fall back to
 * `currentColor`, which is that white here. It is deliberately NOT contrast-measured per
 * colour - on System Yellow a white glyph measures 1.47:1 and Apple ships exactly that.
 * The tile is not the label: the object's name sits beside it at the ink token's full
 * contrast. Under `prefers-contrast: more` the tokens swap to Apple's Accessible column.
 *
 * FOUR SIZES, AND THEY COME FROM THE ROWS THEY SIT IN: `nav` is the desktop sidebar and
 * follows the person's "Sidebar icon size" through `--nav-tile-edge` (Apple's 16 / 20 /
 * 24); `sm` is a fixed 24px for a picker or a gallery; `md` is the fixed 28px of the
 * 44px phone row (`--nav-item-height`); `lg` is the kit's 32pt Service Icon, which is the
 * size `spec/screens/record.md` section 2 item 4 gives the record's identity band and the
 * one the band's own token has declared since round 1 while the band drew the 28 (ledger
 * P5-9). The artwork is inline SVG and scales to any edge, so the 32 is a size and a
 * radius and nothing else - no symbol is redrawn, added or repurposed for it.
 *
 * IT IS NOT INTERACTIVE and it is `aria-hidden`: the row around it is the link, and the
 * row's text is its accessible name. A tile that announced its colour would read "blue"
 * into the middle of a navigation list.
 */
export type ObjectTileSize = 'sm' | 'md' | 'lg' | 'nav';

/**
 * What a row shows on its tile: one of the thirty-one symbols, one of our drawings of Apple's
 * shapes for a row no symbol fits (`ObjectGlyph/MonoSymbol.tsx`, register A5-2), or a hairline icon.
 */
export type ObjectIcon = GlyphName | DrawingName | IconName;

export interface ObjectTileProps {
  readonly icon: ObjectIcon;
  /** One of the twelve system colours, or `null` for the symbol's own pair (graphite for a hairline icon). */
  readonly color: ObjectColor | null;
  /**
   * `nav` is the DESKTOP SIDEBAR tile and the only size that moves: it reads
   * `--nav-tile-*`, which the person's "Sidebar icon size" sets (16 / 20 / 24). `sm` is
   * the fixed 24px tile a picker or a gallery draws, `md` the fixed 28px phone-row one.
   */
  readonly size?: ObjectTileSize | undefined;
  /** PLACEMENT only (D1): margin, `self-*`, `shrink-0`. Never internal layout. */
  readonly className?: string | undefined;
}

/**
 * Edge, radius and hairline-glyph size per size, all from TOKENS (`tokens.css`): the
 * arithmetic that produced them (Apple's 10/57 ratio) lives beside the token.
 */
const SIZES: Record<ObjectTileSize, { readonly box: string; readonly glyph: string }> = {
  sm: { box: 'size-6 rounded-(--radius-tile-sm)', glyph: 'var(--tile-glyph-sm)' },
  md: { box: 'size-7 rounded-(--radius-tile-md)', glyph: 'var(--tile-glyph-md)' },
  lg: { box: 'size-8 rounded-(--radius-tile-lg)', glyph: 'var(--tile-glyph-lg)' },
  nav: {
    box: 'size-(--nav-tile-edge) rounded-(--nav-tile-radius)',
    glyph: 'var(--nav-tile-glyph)',
  },
};

/**
 * THE COLOUR IS HANDED TO THE STYLESHEET AS A PAIR, not painted here (2026-09-09, when
 * the four icon styles arrived). A `tile-fill-*` class sets `--tile-color` and
 * `--tile-color-top` and NOTHING else; `index.css` decides what a tile does with them,
 * because each of Apple's four styles uses the pair differently - Default paints it as
 * the ground, Dark paints the SYMBOL with it over a measured neutral ground, Clear and
 * Tinted replace the ground and keep the pair only for the Multicolour case. Utilities
 * could express the first of those four and none of the rest.
 *
 * The map is written out rather than built as `tile-fill-${color}`: Tailwind and our own
 * `@layer components` are both scanned as source text, so an interpolated class name is
 * never generated, and a map is what makes an unknown colour impossible.
 */
const FILLS: Record<ObjectColor, string> = {
  blue: 'tile-fill-blue',
  green: 'tile-fill-green',
  indigo: 'tile-fill-indigo',
  orange: 'tile-fill-orange',
  pink: 'tile-fill-pink',
  purple: 'tile-fill-purple',
  red: 'tile-fill-red',
  teal: 'tile-fill-teal',
  yellow: 'tile-fill-yellow',
  mint: 'tile-fill-mint',
  cyan: 'tile-fill-cyan',
  brown: 'tile-fill-brown',
};

/**
 * EACH SYMBOL'S OWN TILE, the pair Arif decided per icon (design handoff, section 16).
 * Eight are system colours and share the twelve's tokens; seven are measured on Apple's
 * artwork and have their own (`tokens.css`, "seven pairs that are not system colours").
 */
const SYMBOL_FILLS: Readonly<Record<GlyphName, string> & Partial<Record<DrawingName, string>>> = {
  // the fourteen data objects and the AI assistant (colours decided 2026-09-09, MVP: kit colours only)
  pulse: FILLS.red,
  call: FILLS.teal,
  creator: 'tile-fill-creator',
  person: 'tile-fill-gray',
  team: FILLS.brown,
  agency: FILLS.indigo,
  lead: FILLS.orange,
  deal: FILLS.purple,
  enrollment: FILLS.mint,
  gameplan: FILLS.indigo,
  payment: FILLS.green,
  win: FILLS.blue,
  phase: FILLS.cyan,
  delivery: FILLS.brown,
  ai: 'tile-fill-ai',
  // the function rows, one colour per family: overview cyan, review indigo, administration gray,
  // connections blue, automation purple, status green (Arif 2026-09-09)
  dashboard: FILLS.cyan,
  reports: FILLS.cyan,
  tasks: FILLS.indigo,
  pending: FILLS.indigo,
  objects: 'tile-fill-gray',
  settings: 'tile-fill-gray',
  // The sidebar toggle draws its symbol in the toolbar's ink and never on a tile; it has a
  // pair because every symbol does, and administration gray is the family it belongs to.
  sidebar: 'tile-fill-gray',
  workflows: FILLS.purple,
  imports: FILLS.blue,
  exports: FILLS.blue,
  webhooks: FILLS.blue,
  inbound: FILLS.blue,
  health: FILLS.green,
  members: 'tile-fill-gray',
  identity: 'tile-fill-gray',
  instance: 'tile-fill-gray',
  // The destinations that took a drawing of their own (2026-10-05, register A7-S2, A8-1 b, A8-2)
  // keep the colour their row had, by the same rules: Clients is the coaches' view on the agency
  // records (A8-1), so the agency's indigo; My tasks stays in the review family's indigo; Users
  // stays in the administration family's gray. The shape is what tells the destinations apart.
  clients: FILLS.indigo,
  clipboard: FILLS.indigo,
  key: 'tile-fill-gray',
  // The Oliver AI pane (A8-3) is the bot that writes in the client groups on its own: the
  // automation family's purple. The Siri-white pair stays the CRM's own assistant's (`ai`).
  bot: FILLS.purple,
};

function fill(icon: ObjectIcon, color: ObjectColor | null): string {
  if (color !== null) return FILLS[color];
  if (isGlyphName(icon) || isDrawingName(icon)) return SYMBOL_FILLS[icon] ?? 'tile-fill-none';
  return 'tile-fill-none';
}

export function ObjectTile({ icon, color, size = 'sm', className }: ObjectTileProps) {
  const shape = SIZES[size];
  return (
    <span
      aria-hidden="true"
      /**
       * `overflow-hidden` is what clips a symbol's halo at the tile's rounded corner
       * rather than at the SVG box: the glyph is drawn to the edge and its shadow
       * reaches past it, exactly as Apple's artwork is cut by the icon mask.
       */
      className={cx(
        'object-tile inline-flex shrink-0 items-center justify-center overflow-hidden',
        shape.box,
        fill(icon, color),
        className,
      )}
    >
      {isGlyphName(icon) ? (
        <ObjectGlyph name={icon} size="100%" effects />
      ) : isDrawingName(icon) ? (
        /* A drawing stands on the tile in the hairline icon's box, as Apple's white symbol
           stands on a System Settings tile: it is a symbol, not artwork drawn to the edge. */
        <MonoSymbol name={icon} size={shape.glyph} />
      ) : (
        <Icon name={icon} size={shape.glyph} />
      )}
    </span>
  );
}
